import { flexRender, type Table } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useTranslate } from "@tolgee/react";
import { ArrowDownIcon, ArrowUpIcon, ChevronsUpDownIcon } from "lucide-react";
import { type UIEvent, useState } from "react";
import { TableErrorState } from "#/components/ui/table-error-state";

type VirtualizedGridTableProps<TData> = {
	table: Table<TData>;
	gridClass: string;
	estimateSize: number;
	emptyMessage?: string;
	onScrollNearEnd?: () => void;
	isLoadingMore?: boolean;
	isLoading?: boolean;
	isError?: boolean;
	errorMessage?: string;
	onRetry?: () => void;
};

export function VirtualizedGridTable<TData>({
	table,
	gridClass,
	estimateSize,
	emptyMessage,
	onScrollNearEnd,
	isLoadingMore,
	isLoading,
	isError,
	errorMessage,
	onRetry,
}: VirtualizedGridTableProps<TData>) {
	const { t } = useTranslate();
	const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
		null,
	);
	const rows = table.getRowModel().rows;

	const virtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => scrollElement,
		estimateSize: () => estimateSize,
		overscan: 10,
	});

	return (
		<div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-auto">
			<div className="flex min-h-0 w-max min-w-full flex-1 flex-col">
				<div
					className={`${gridClass} px-4 py-3 text-label2-medium uppercase text-trade-text-secondary`}
				>
					{table.getHeaderGroups().map((headerGroup) =>
						headerGroup.headers.map((header) =>
							header.column.getCanSort() ? (
								<button
									key={header.id}
									type="button"
									className="inline-flex cursor-pointer items-center gap-1 uppercase select-none text-left"
									onClick={header.column.getToggleSortingHandler()}
								>
									{flexRender(
										header.column.columnDef.header,
										header.getContext(),
									)}
									{header.column.getIsSorted() === "asc" ? (
										<ArrowUpIcon className="size-3" />
									) : header.column.getIsSorted() === "desc" ? (
										<ArrowDownIcon className="size-3" />
									) : (
										<ChevronsUpDownIcon className="size-3 opacity-40" />
									)}
								</button>
							) : (
								<span key={header.id}>
									{flexRender(
										header.column.columnDef.header,
										header.getContext(),
									)}
								</span>
							),
						),
					)}
				</div>

				<div
					ref={setScrollElement}
					className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-2 pb-2"
					onScroll={
						onScrollNearEnd
							? (e: UIEvent<HTMLDivElement>) => {
									const t = e.currentTarget;
									if (t.scrollHeight - t.scrollTop - t.clientHeight < 200)
										onScrollNearEnd();
								}
							: undefined
					}
				>
					{rows.length === 0 && isError ? (
						<TableErrorState
							message={errorMessage}
							onRetry={onRetry}
							className="text-trade-text-faint"
						/>
					) : rows.length === 0 && isLoading ? (
						<div className="py-6 text-center text-label2 text-trade-text-faint">
							{t("state.loading")}
						</div>
					) : rows.length === 0 && emptyMessage ? (
						<div className="py-6 text-center text-label2 text-trade-text-faint">
							{emptyMessage}
						</div>
					) : (
						<div
							style={{
								height: virtualizer.getTotalSize(),
								position: "relative",
							}}
						>
							{virtualizer.getVirtualItems().map((virtualRow) => {
								const row = rows[virtualRow.index];
								return (
									<div
										key={row.id}
										data-index={virtualRow.index}
										ref={virtualizer.measureElement}
										style={{
											position: "absolute",
											top: 0,
											left: 0,
											right: 0,
											transform: `translateY(${virtualRow.start}px)`,
										}}
									>
										<div
											className={`${gridClass} items-center rounded-lg px-3 py-1.5 text-label2 hover:bg-trade-interactive`}
										>
											{row.getVisibleCells().map((cell) => (
												<span key={cell.id}>
													{flexRender(
														cell.column.columnDef.cell,
														cell.getContext(),
													)}
												</span>
											))}
										</div>
									</div>
								);
							})}
						</div>
					)}
					{isLoadingMore && (
						<div className="py-3 text-center text-label2 text-trade-text-faint">
							{t("state.loading-more")}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
